import React, { Children, createElement, forwardRef, isValidElement } from "react"
import classNames from "classnames"
import { MutuallyExclude } from "@utils/mutuallyExclusive"
import { TAllowedTextStyles, allowedTextStyles, bodyStyles, headingStyles, largeStyles, smallStyles } from "./Text.config"
import "./Text.css"
import { generateMarginClasses } from "@utils/spacing"

/** The default colour choices for text. All others should be controlled using CSS. */
const colourChoices = ["default", "gray", "on-dark", "secondary-main", "error", "highly-unlikely", "unlikely", "possibly", "likely", "highly-likely", "primary-main", "tertiary-main"]
const textAlignChoices = ["center", "left", "right", "justify"]
const baseClass = "wb-text"

type BaseProps = {
  /** Pass through classname for customising the style of the text. */
  className?: string,

  /** Override the HTML tag used to render the text node.
     * By default, heading and subtitle styles are rendered in a `<div>`
     * (to make them block-level), body styles are rendered in a `<p>` and
     * variety styles in a `<span>`.
     *
     * Note that the heading style chosen should not prescribe which `<h*>`
     * tag to render the text node as – whether you choose `<h1>`, `<h2>` or
     * `<h5>` should only depend on the semantic level of the given title in
     * the context of the whole page. */
  as?: string,

  /** If set, each direct child of the Text component will be treated as a separate
   * paragraph, with spacing applied. This only makes a difference for body styles:
   * body, bodySmall and bodyLarge.
   */
  multiParagraph?: boolean,

  /** A convenience prop for applying the default black or medium gray colour –
   * the two main colours we use for body text. Any other colours should be
   * controlled via CSS.
   *
   * Furthermore, `colour` is intentionally not given a default value, and
   * therefore if not specified, `<Text>` will take on the colour of it's nearest
   * parent that defines it. */
  colour?: typeof colourChoices[number],

  /** Margin is a shorthand prop for applying margin to the text. */
  margin?: string | object,  // Deprecated

  /** These correspond to the text-align css property.
   * The list is non-exhaustive and includes common options only */
  textAlign?: typeof textAlignChoices[number],

  /** Responsive indicates it should dynamically adjust it's font size and
   * line-height according to the viewport size. Typically one or at most two
   * breakpoints change the size */
  responsive?: boolean,

  /** React children. */
  children?: React.ReactNode,
}

type TextStyle = MutuallyExclude<TAllowedTextStyles, keyof TAllowedTextStyles>

type InlineTextConfigProps = BaseProps & {
  textStyle: string,
  text: string
}

type SingleTextConfigProps = BaseProps & {
  textStyle: string,
  text: string,
  inline?: InlineTextConfigProps | InlineTextConfigProps[]
}

type ConfigProps = SingleTextConfigProps | SingleTextConfigProps[]

type TextProps = BaseProps & TextStyle & {
  config?: ConfigProps
}
/**
 * A simple component that applies the selected text style CSS properties to the
 * text contained inside.
 */
const Text = forwardRef(({
  className,
  as,
  multiParagraph,
  colour,
  textAlign,
  margin,
  responsive = false,
  config,
  children,
  ...textStyle
}: TextProps, ref) => {
  // ESLint prop-type validator is broken with complex prop types
  /* eslint-disable react/prop-types */

  // Deprecated
  const marginClasses = generateMarginClasses(margin)

  const forwardedProps = { className, textAlign, colour, responsive, as }
  if (config) {
    return renderTextFromConfig(config, forwardedProps)
  }

  const _textStyle = getTextStyleFromProps(textStyle)
  const passThroughProps = { ...getPassThroughTextStyle(textStyle), as, children, multiParagraph, colour, textAlign, responsive, className }

  const classes = classNames(
    baseClass,
    marginClasses,
    `${baseClass}--${_textStyle}`,
    passThroughProps.className, {
    [`${baseClass}--colour-${colour}`]: colour,
    [`${baseClass}--responsive`]: responsive
  }
  )
  const tagName = tagNameForTextStyle(_textStyle, multiParagraph)
  const _multiParagraph = multiParagraph && isBodyStyle(_textStyle)

  const _children = multiParagraph ? (
    Children.toArray(children).map((child, i) => (
      <p key={i}>{isValidElement(child) && child.props.children || child}</p>
    ))
  ) : (
    children
  )

  return createElement(
    as || tagName,
    { ...passThroughProps, className: classes, style: { textAlign }, ref },
    _children
  )
})

Text.displayName = "Text"

/** Helper methods for the Text component. */

function getPassThroughTextStyle(props: TextStyle) {

  const isPassThroughProp = propName => (
    ![...(allowedTextStyles)].includes(propName)
  )

  return Object.fromEntries(
    Object.entries(props).map(([propName, propValue]) => (
      isPassThroughProp(propName) ? [propName, propValue] : []
    )).filter(Boolean)
  )
}

function getTextStyleFromProps(props) {
  const specifiedTextStyle = allowedTextStyles.find(textStyleName => props[textStyleName])
  return specifiedTextStyle || "body"
}

function tagNameForTextStyle(textStyle, multiParagraph) {
  if (isBodyStyle(textStyle)) {
    return multiParagraph ? "div" : "p"
  }

  if (isHeadingStyle(textStyle)) {
    return "div"
  }

  return "span"
}

function isBodyStyle(textStyle) {
  return bodyStyles.includes(textStyle)
}

function isHeadingStyle(textStyle) {
  return headingStyles.includes(textStyle)
}

export function iconSizeForTextStyle(textStyle) {
  if (smallStyles.includes(textStyle)) {
    return "small"
  }

  if (isHeadingStyle(textStyle) || largeStyles.includes(textStyle)) {
    return "large"
  }

  return "medium"
}

/**
 * Render a single line or multiple lines of copy using the <Text> component.
 * Passing an object or array of a single item will render a single <Text>
 * component, whereas passing in an array with 2+ items will render multiple
 * <Text> components.
 *
 * Specify the text, textStyle, and inline to render text within the rendered
 * <Text> component; any additional props will be passed to <Text>. For example:
 *
 * ```
 * <Text config={{
 *   text: "Hello",
 *   textStyle: "heading2a",
 *   as: "p",
 *   inline: [{
 *     text: " world!",
 *     textStyle: "heading2b"
 *   }]
 * }}/>
 * // Renders equivalent to:
 * <Text heading2a as="p">
 *   Hello<Text heading2b as="span"> world!</Text>
 * </Text>
 * ``` */
function renderTextFromConfig(config: ConfigProps, additionalProps: Partial<BaseProps>, depth = 0) {
  const lines = config instanceof Array ? config : [config]

  return lines.map(({ text, textStyle, inline, ...rest }, index) => {
    const textProps = {
      [textStyle]: true,
      className: additionalProps.className,
      textAlign: additionalProps.textAlign,
      colour: additionalProps.colour,
      responsive: additionalProps.responsive,
      as: additionalProps.as
    }
    const inlineText = inline && (inline instanceof Array ? inline : [inline]).map(line => ({ as: "span", ...line }))

    const { children, ...passThroughProps } = rest

    return (
      <Text key={`${depth}-${index}`} {...textProps} {...passThroughProps}>
        {text}
        {inlineText && renderTextFromConfig(inlineText, additionalProps, depth + 1)}
      </Text>
    )
  })
}

export { Text }
export { getTextStyleFromProps }
export type { BaseProps, TextProps, TextStyle, ConfigProps, SingleTextConfigProps, InlineTextConfigProps }
